import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Crown, Medal, Share2, Swords } from 'lucide-react';
import { api } from '../lib/api';
import { SectionLabel, Reveal } from '../components/Section';
import { shareRankCard } from '../lib/rankCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_ENTRIES = [
  { rank: 1, name: 'ACE Hunters', points: 128, eliminations: 34, matches_played: 4, winnings: 500 },
  { rank: 2, name: 'Team REAPERS', points: 113, eliminations: 29, matches_played: 4, winnings: 300 },
  { rank: 3, name: 'LG ESPORTS', points: 98, eliminations: 26, matches_played: 4, winnings: 200 },
  { rank: 4, name: 'Team Targaryen', points: 88, eliminations: 21, matches_played: 3, winnings: 0 },
  { rank: 5, name: 'TeamXlegions', points: 80, eliminations: 18, matches_played: 3, winnings: 0 },
];

function Toggle({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex gap-2">
      {options.map((o) => (
        <button
          key={o.key}
          data-testid={`${testidPrefix}-${o.key}`}
          onClick={() => onChange(o.key)}
          className={`relative px-4 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-colors duration-300 ${
            value === o.key ? 'text-white' : 'text-[#8a8a8a] hover:text-white border border-white/10'
          }`}
        >
          {value === o.key && (
            <motion.span
              layoutId={`${testidPrefix}-pill`}
              className="absolute inset-0 bg-[#e8173a] rounded-full"
              transition={{ type: 'spring', bounce: 0.25, duration: 0.5 }}
            />
          )}
          <span className="relative">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export default function Leaderboard() {
  const [period, setPeriod] = useState('weekly');
  const [mode, setMode] = useState('squad');
  const [data, setData] = useState(null);
  const [shareMenu, setShareMenu] = useState(null);
  const isDemo = new URLSearchParams(window.location.search).get('demo') === '1';

  const share = (e, format) => {
    setShareMenu(null);
    shareRankCard(
      {
        rank: e.rank,
        name: e.name,
        points: e.points,
        eliminations: e.eliminations,
        winnings: e.winnings,
        periodLabel: `${data?.period_key || ''} ${mode} leaderboard`,
      },
      format
    );
  };

  useEffect(() => {
    if (isDemo) {
      setData({ period_key: '2026-W37 · PREVIEW', entries: DEMO_ENTRIES });
      return;
    }
    setData(null);
    axios
      .get(`${API}/leaderboard`, { params: { period, match_type: mode } })
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], period_key: '' }));
  }, [period, mode, isDemo]);

  const entries = data?.entries || [];
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <main className="min-h-screen" data-testid="leaderboard-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <SectionLabel testid="leaderboard-label">Leaderboard</SectionLabel>
        <Reveal>
          <h1 className="mt-5 font-display text-white text-4xl sm:text-6xl">
            Kings of the <span className="text-[#e8173a]">Arena</span>
          </h1>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-4 text-[#9e9e9e] text-sm sm:text-base max-w-lg">
            {data?.period_key ? `Current standings — ${data.period_key}. ` : ''}
            Ranks update live as tournament results are verified.
          </p>
        </Reveal>

        <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="leaderboard-filters">
          <Toggle
            options={[{ key: 'weekly', label: 'Weekly' }, { key: 'monthly', label: 'Monthly' }]}
            value={period}
            onChange={setPeriod}
            testidPrefix="leaderboard-period"
          />
          <Toggle
            options={[{ key: 'solo', label: 'Solo' }, { key: 'squad', label: 'Squad' }]}
            value={mode}
            onChange={setMode}
            testidPrefix="leaderboard-mode"
          />
        </div>

        {data === null ? (
          <div className="mt-10 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-[#1a1f2e] animate-pulse" data-testid={`leaderboard-skeleton-${i}`} />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <Reveal>
            <div className="mt-10 card-surface p-12 text-center" data-testid="leaderboard-empty">
              <span className="mx-auto w-14 h-14 rounded-xl bg-[#e8173a]/10 border border-[#e8173a]/30 flex items-center justify-center">
                <Swords className="w-7 h-7 text-[#e8173a]" />
              </span>
              <p className="mt-5 text-white font-bold text-lg">No ranked players yet</p>
              <p className="mt-2 text-sm text-[#8a8a8a] max-w-sm mx-auto">
                The board resets every period — the first battles are being fought right now. Win a {mode} tournament to claim rank one.
              </p>
            </div>
          </Reveal>
        ) : (
          <>
            <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-5">
              {top3.map((e, i) => (
                <Reveal key={e.rank} delay={i * 0.1}>
                  <div
                    data-testid={`leaderboard-podium-${e.rank}`}
                    className={`card-surface p-4 sm:p-6 text-center relative overflow-hidden ${
                      i === 0 ? 'border-[#e8173a]/50 sm:-translate-y-3' : ''
                    }`}
                  >
                    {i === 0 && <div className="absolute inset-0 bg-[#e8173a]/10 pointer-events-none" />}
                    <button
                      data-testid={`share-rank-${e.rank}`}
                      onClick={() => setShareMenu(shareMenu === e.rank ? null : e.rank)}
                      aria-label={`Share rank ${e.rank} card`}
                      className="absolute top-2.5 right-2.5 w-8 h-8 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center text-[#9e9e9e] hover:text-white hover:border-[#e8173a]/60 transition-colors duration-200"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                    <AnimatePresence>
                      {shareMenu === e.rank && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          className="absolute top-12 right-2.5 z-20 bg-[#1a1f2e] rounded-lg overflow-hidden border border-white/10 shadow-2xl"
                          data-testid={`share-menu-${e.rank}`}
                        >
                          <button
                            data-testid={`share-rank-${e.rank}-square`}
                            onClick={() => share(e, 'square')}
                            className="block w-full text-left px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#c9c9c9] hover:text-white hover:bg-[#e8173a]/15 whitespace-nowrap transition-colors"
                          >
                            Square Post 1:1
                          </button>
                          <button
                            data-testid={`share-rank-${e.rank}-story`}
                            onClick={() => share(e, 'story')}
                            className="block w-full text-left px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-[#c9c9c9] hover:text-white hover:bg-[#e8173a]/15 whitespace-nowrap transition-colors border-t border-white/5"
                          >
                            Story 9:16
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <span
                      className={`mx-auto w-10 h-10 rounded-lg flex items-center justify-center font-black ${
                        i === 0 ? 'bg-[#e8173a] text-white' : 'bg-white/10 text-white'
                      }`}
                    >
                      {i === 0 ? <Crown className="w-5 h-5" /> : e.rank}
                    </span>
                    <p className="mt-3 text-white font-bold text-sm truncate">{e.name}</p>
                    <p className="mt-1 text-[#e8173a] font-extrabold italic">{e.points} pts</p>
                    {e.winnings > 0 && <p className="text-[#00c853] text-xs font-bold mt-0.5">₹{e.winnings.toLocaleString('en-IN')}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
            {rest.length > 0 && (
              <div className="mt-8 card-surface divide-y divide-white/5" data-testid="leaderboard-table">
                {rest.map((e, i) => (
                  <Reveal key={e.rank} delay={i * 0.03}>
                    <div className="flex items-center gap-4 px-5 sm:px-7 py-4" data-testid={`leaderboard-row-${e.rank}`}>
                      <span className="w-8 text-center text-[#8a8a8a] font-black italic">{e.rank}</span>
                      <span className="flex-1 text-white font-semibold text-sm truncate">{e.name}</span>
                      <span className="text-xs text-[#8a8a8a] hidden sm:inline">{e.matches_played} matches</span>
                      <span className="text-xs text-[#8a8a8a]">{e.eliminations} elims</span>
                      <span className="text-[#e8173a] font-extrabold italic text-sm w-16 text-right">{e.points} pts</span>
                      <button
                        data-testid={`share-rank-row-${e.rank}`}
                        onClick={() =>
                          shareRankCard({
                            rank: e.rank,
                            name: e.name,
                            points: e.points,
                            eliminations: e.eliminations,
                            winnings: e.winnings,
                            periodLabel: `${data?.period_key || ''} ${mode} leaderboard`,
                          })
                        }
                        aria-label={`Share rank ${e.rank} card`}
                        className="w-7 h-7 rounded-md border border-white/10 flex items-center justify-center text-[#8a8a8a] hover:text-[#e8173a] hover:border-[#e8173a]/60 transition-colors duration-200 shrink-0"
                      >
                        <Share2 className="w-3 h-3" />
                      </button>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}

        <p className="mt-8 text-xs text-[#5c5c5c] flex items-center gap-2">
          <Medal className="w-3.5 h-3.5 text-[#e8173a]" />
          Bonus prizes for weekly and monthly leaders are credited automatically in the app.
        </p>
      </div>
    </main>
  );
}
